import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Clock, Flame, Lock, Share2, Camera, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const Workouts = () => {
  const [workouts, setWorkouts] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [completedWorkoutData, setCompletedWorkoutData] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [expandedPhotos, setExpandedPhotos] = useState({});
  const [expandedCards, setExpandedCards] = useState({});

  const fetchData = async () => {
    try {
      const [workoutsRes, progressRes] = await Promise.all([
        axios.get(`/api/workouts`, { withCredentials: true }),
        axios.get(`/api/workouts/progress`, { withCredentials: true })
      ]);
      setWorkouts(workoutsRes.data);
      setProgress(progressRes.data);
      
      if (!selectedLevel && progressRes.data) {
        setSelectedLevel(progressRes.data.current_level);
      }

      // Auto-expand the first uncompleted workout
      const completedIds = progressRes.data?.completed_workout_ids || [];
      const firstUncompleted = workoutsRes.data.find(w => !completedIds.includes(w.workout_id));
      if (firstUncompleted) {
        setExpandedCards(prev => ({ ...prev, [firstUncompleted.workout_id]: true }));
      }
    } catch (error) {
      toast.error('Failed to fetch workouts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCompleteWorkout = async (workoutId) => {
    try {
      const workout = workouts.find(w => w.workout_id === workoutId);
      const response = await axios.post(
        `/api/workouts/complete`,
        { workout_id: workoutId },
        { withCredentials: true }
      );
      
      setCompletedWorkoutData({
        workoutTitle: workout.title,
        level: response.data.level,
        badge: response.data.badge,
        points: response.data.points,
        leveledUp: response.data.leveled_up
      });
      
      if (response.data.leveled_up) {
        if (response.data.newly_unlocked_workouts > 0) {
          toast.success(`Level Up! You're now ${response.data.badge} - Level ${response.data.level}! ${response.data.newly_unlocked_workouts} new workouts unlocked!`);
        } else {
          toast.success(`Level Up! You're now ${response.data.badge} - Level ${response.data.level}!`);
        }
      } else {
        toast.success(`Workout completed! +10 points`);
      }
      
      setShareDialogOpen(true);
      
      // Collapse the just-completed workout and refresh
      setExpandedCards(prev => ({ ...prev, [workoutId]: false }));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete workout');
    }
  };

  const handleShareToInstagram = async () => {
    if (!completedWorkoutData) return;

    const shareText = `Just completed "${completedWorkoutData.workoutTitle}" on Iron Dad's Dad Bod to Beast program!\n\nLevel ${completedWorkoutData.level} - ${completedWorkoutData.badge}\n${completedWorkoutData.points} Total Points\n\n${completedWorkoutData.leveledUp ? 'LEVEL UP! ' : ''}Become the father your kids look up to!\n\n#IronDad #DadBodToBeast #FitDad #DadFitness #WorkoutComplete`;

    await copyToClipboard(shareText);

    if (navigator.share) {
      try {
        await navigator.share({ title: 'Iron Dad Achievement', text: shareText });
        setShareDialogOpen(false);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.log('Share failed, but text already copied to clipboard');
        }
      }
    }
  };

  const copyToClipboard = async (text) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        toast.success('Text copied! Now you can paste it in Instagram.', { duration: 4000 });
      } catch (error) {
        fallbackCopyToClipboard(text);
      }
    } else {
      fallbackCopyToClipboard(text);
    }
  };

  const fallbackCopyToClipboard = (text) => {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      document.execCommand('copy');
      toast.success('Text copied! Now you can paste it in Instagram.', { duration: 4000 });
    } catch (err) {
      toast.error('Please manually copy the text from the dialog');
    }
    document.body.removeChild(textArea);
  };

  const isWorkoutCompleted = (workoutId) => {
    return progress?.completed_workout_ids?.includes(workoutId);
  };

  const isWorkoutLocked = (workout, index) => {
    if (index === 0) return false;
    // Check if ALL previous workouts in the list are completed
    for (let i = 0; i < index; i++) {
      if (!isWorkoutCompleted(workouts[i].workout_id)) {
        return true;
      }
    }
    return false;
  };

  const toggleCard = (workoutId) => {
    setExpandedCards(prev => ({ ...prev, [workoutId]: !prev[workoutId] }));
  };

  const handleLevelSelect = async (level) => {
    setSelectedLevel(level);
    try {
      const response = await axios.get(`/api/workouts/by-level/${level}`, { 
        withCredentials: true 
      });
      setWorkouts(response.data);
      
      // Auto-expand first uncompleted workout in this level
      const completedIds = progress?.completed_workout_ids || [];
      const firstUncompleted = response.data.find(w => !completedIds.includes(w.workout_id));
      const newExpanded = {};
      if (firstUncompleted) {
        newExpanded[firstUncompleted.workout_id] = true;
      }
      setExpandedCards(newExpanded);
      setExpandedPhotos({});
    } catch (error) {
      toast.error('Failed to load workouts for this level');
    }
  };

  const badges = [
    { level: 1, name: 'Alpha Dad', icon: '🥉', unlocked: progress?.current_level >= 1 },
    { level: 2, name: 'Relentless Dad', icon: '⚔️', unlocked: progress?.current_level >= 2 },
    { level: 3, name: 'Beast Mode', icon: '🏆', unlocked: progress?.current_level >= 3 },
    { level: 4, name: 'Savage', icon: '👑', unlocked: progress?.current_level >= 4 },
    { level: 5, name: 'Unbreakable', icon: '🔥', unlocked: progress?.current_level >= 5 },
    { level: 6, name: 'Iron Dad', icon: '⚡', unlocked: progress?.current_level >= 6 }
  ];

  const difficultyColors = {
    'Alpha Dad': 'text-green-400',
    'Intermediate': 'text-yellow-400',
    'Advanced': 'text-red-400'
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-2">Workouts</h1>
          <p className="text-base md:text-lg text-muted-foreground">Complete workouts in order to level up and earn badges</p>
        </div>

        {/* Equipment Info Card */}
        <Card className="mb-6 bg-card/50 border-primary/30">
          <CardHeader>
            <CardTitle className="text-xl font-bold text-primary uppercase flex items-center gap-2">
              <span>💪</span> Equipment Needed
            </CardTitle>
            <CardDescription>Low cost, minimal, effective equipment for all workouts</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
              <div className="flex items-center gap-2"><span>🪢</span><span>1x Skipping rope</span></div>
              <div className="flex items-center gap-2"><span>💪</span><span>Dumbbells (5-8kg)</span></div>
              <div className="flex items-center gap-2"><span>🏋️</span><span>Dumbbells (10-25kg)</span></div>
              <div className="flex items-center gap-2"><span>⚫</span><span>Kettlebells (10-24kg)</span></div>
              <div className="flex items-center gap-2"><span>⚽</span><span>Medicine Ball (8-12kg)</span></div>
            </div>
          </CardContent>
        </Card>

        {/* Level Badges Filter */}
        {progress && (
          <div className="mb-6">
            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-3">Select Level</h3>
            <div className="flex flex-wrap gap-3">
              {badges.map((badge) => (
                <button
                  key={badge.level}
                  data-testid={`level-badge-${badge.level}`}
                  onClick={() => badge.unlocked && handleLevelSelect(badge.level)}
                  disabled={!badge.unlocked}
                  className={`flex items-center gap-2 px-4 py-3 rounded-sm border transition-all duration-200 ${
                    badge.unlocked
                      ? selectedLevel === badge.level
                        ? 'bg-primary border-primary text-white font-bold'
                        : 'bg-card border-border hover:border-primary cursor-pointer'
                      : 'bg-zinc-900/30 border-zinc-800 opacity-50 cursor-not-allowed'
                  }`}
                >
                  <span className="text-2xl">{badge.icon}</span>
                  <div className="text-left">
                    <div className="text-xs font-bold uppercase">Level {badge.level}</div>
                    <div className="text-sm">{badge.name}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Share Dialog */}
        <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle>Share Your Achievement!</DialogTitle>
              <DialogDescription>Let everyone know about your progress on Instagram</DialogDescription>
            </DialogHeader>
            {completedWorkoutData && (
              <div className="space-y-4">
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 text-center">
                  <div className="text-4xl mb-3">{completedWorkoutData.leveledUp ? '🎉' : '💪'}</div>
                  <h3 className="text-xl font-bold mb-2">{completedWorkoutData.workoutTitle}</h3>
                  {completedWorkoutData.leveledUp && <p className="text-accent font-bold mb-2">LEVEL UP!</p>}
                  <p className="text-muted-foreground">Level {completedWorkoutData.level} - {completedWorkoutData.badge}</p>
                  <p className="text-primary font-semibold mt-2">{completedWorkoutData.points} Total Points</p>
                </div>
                <Button
                  data-testid="share-instagram-button"
                  onClick={handleShareToInstagram}
                  className="w-full bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white hover:opacity-90 rounded-sm font-bold uppercase tracking-wider h-12"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  Share Achievement
                </Button>
                <Button
                  data-testid="skip-share-button"
                  onClick={() => setShareDialogOpen(false)}
                  className="w-full bg-secondary text-white hover:bg-secondary/80 rounded-sm font-medium h-12"
                >
                  Skip for Now
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Workouts List */}
        {workouts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-4">Loading your first workout...</p>
            <p className="text-sm text-muted-foreground">If workouts don't appear, please refresh the page</p>
          </div>
        ) : (
          <div className="space-y-4">
            {workouts.map((workout, index) => {
              const completed = isWorkoutCompleted(workout.workout_id);
              const locked = isWorkoutLocked(workout, index);
              const isExpanded = expandedCards[workout.workout_id] || false;

              return (
                <motion.div
                  key={workout.workout_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.03 }}
                >
                  <Card
                    data-testid={`workout-card-${workout.workout_id}`}
                    className={`bg-card border-border transition-all duration-300 ${
                      locked ? 'opacity-60' : completed ? 'border-green-800/50' : 'border-primary/30'
                    }`}
                  >
                    {/* Collapsed Header - Always Visible */}
                    <button
                      data-testid={`workout-toggle-${workout.workout_id}`}
                      onClick={() => !locked && toggleCard(workout.workout_id)}
                      className={`w-full text-left px-6 py-4 flex items-center gap-4 ${locked ? 'cursor-not-allowed' : 'cursor-pointer hover:bg-zinc-800/30'} transition-colors duration-200`}
                    >
                      <div className="flex-shrink-0">
                        {locked ? (
                          <div className="w-10 h-10 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                            <Lock className="w-5 h-5 text-zinc-500" />
                          </div>
                        ) : completed ? (
                          <div className="w-10 h-10 rounded-full bg-green-900/50 border border-green-700 flex items-center justify-center">
                            <Check className="w-5 h-5 text-green-400" />
                          </div>
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-primary/20 border border-primary/50 flex items-center justify-center">
                            <span className="text-primary font-bold text-sm">{index + 1}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-bold text-lg ${locked ? 'text-zinc-500' : ''}`}>{workout.title}</h3>
                          {completed && (
                            <span className="text-xs font-bold uppercase tracking-wider text-green-400 bg-green-900/30 px-2 py-0.5 rounded-full border border-green-800/50">
                              Completed
                            </span>
                          )}
                          {locked && (
                            <span className="text-xs font-bold uppercase tracking-wider text-zinc-500 bg-zinc-800 px-2 py-0.5 rounded-full border border-zinc-700">
                              Locked
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{workout.duration_minutes} min</span>
                          <span className="flex items-center gap-1"><Flame className="w-3 h-3" />{workout.difficulty}</span>
                          <span className="bg-zinc-800 text-zinc-400 px-2 py-0.5 rounded-full text-xs">{workout.category}</span>
                        </div>
                      </div>
                      {!locked && (
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                      )}
                    </button>

                    {/* Expanded Content */}
                    <AnimatePresence>
                      {isExpanded && !locked && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-6 border-t border-border/50">
                            {/* Workout Description */}
                            <div className="mt-4">
                              <CardDescription className="whitespace-pre-line text-sm leading-relaxed">{workout.description}</CardDescription>
                            </div>

                            {/* Exercise Demo Photos - Collapsible */}
                            {workout.exercise_photos && Object.keys(workout.exercise_photos).length > 0 && (
                              <div className="mt-4">
                                <button
                                  data-testid={`toggle-photos-${workout.workout_id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedPhotos(prev => ({...prev, [workout.workout_id]: !prev[workout.workout_id]}));
                                  }}
                                  className="flex items-center gap-2 w-full text-left py-2 px-3 rounded-sm bg-primary/10 border border-primary/30 hover:bg-primary/20 transition-colors duration-200"
                                >
                                  <Camera className="w-4 h-4 text-primary" />
                                  <span className="text-sm font-semibold text-primary flex-1">
                                    Exercise Demo Photos ({Object.keys(workout.exercise_photos).length})
                                  </span>
                                  <ChevronDown className={`w-4 h-4 text-primary transition-transform duration-200 ${expandedPhotos[workout.workout_id] ? 'rotate-180' : ''}`} />
                                </button>
                                <AnimatePresence>
                                  {expandedPhotos[workout.workout_id] && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: 'auto', opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.3 }}
                                      className="overflow-hidden"
                                    >
                                      <div className="mt-3 space-y-4">
                                        {Object.entries(workout.exercise_photos).map(([exerciseName, photos]) => (
                                          <div key={exerciseName} className="border border-border rounded-sm p-3 bg-zinc-900/50">
                                            <p className="text-xs font-bold text-white uppercase tracking-wider mb-2">{exerciseName}</p>
                                            <div className="grid grid-cols-2 gap-2">
                                              {photos.map((photoUrl, idx) => (
                                                <img
                                                  key={idx}
                                                  src={photoUrl}
                                                  alt={`${exerciseName} step ${idx + 1}`}
                                                  className="w-full h-48 sm:h-64 object-contain rounded-sm border border-border bg-black"
                                                  data-testid={`exercise-photo-${exerciseName}-${idx}`}
                                                  loading="lazy"
                                                />
                                              ))}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </div>
                            )}

                            {/* Complete Button */}
                            <div className="mt-6">
                              <Button
                                data-testid={`complete-workout-button-${workout.workout_id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCompleteWorkout(workout.workout_id);
                                }}
                                disabled={completed}
                                className="w-full bg-primary text-white hover:bg-primary/90 rounded-sm font-bold uppercase tracking-wider disabled:opacity-50 disabled:cursor-not-allowed h-12"
                              >
                                {completed ? (
                                  <><Check className="w-5 h-5 mr-2" /> Workout Completed</>
                                ) : (
                                  'Complete Workout'
                                )}
                              </Button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Workouts;
